import { motion } from "framer-motion";
import { Trophy, Award, Zap, Skull, Map as MapIcon, Flag, Star, Lock } from "lucide-react";

const ICONS = { Trophy, Award, Zap, Skull, Map: MapIcon, Flag, Star };

const TIER_RING = {
  bronze: "ring-amber-700/40 from-amber-700/30 to-amber-900/10 text-amber-300",
  silver: "ring-slate-400/40 from-slate-300/30 to-slate-500/10 text-slate-100",
  gold:   "ring-yellow-300/50 from-yellow-300/30 to-amber-600/10 text-yellow-200",
};

function AchievementTile({ a, unlocked, progress }) {
  const Icon = ICONS[a.icon] || Trophy;
  const tier = TIER_RING[a.tier] || TIER_RING.bronze;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`achievement-${a.slug}-${unlocked ? "unlocked" : "locked"}`}
      className={`relative rounded-2xl p-4 ring-1 bg-gradient-to-br border transition ${
        unlocked
          ? `${tier} border-white/5 shadow-[0_8px_30px_-12px_rgba(255,255,255,0.2)]`
          : "ring-white/5 from-zinc-900/40 to-zinc-900/20 text-zinc-500 border-white/5 grayscale"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${unlocked ? "bg-black/30" : "bg-black/40"}`}>
          {unlocked ? <Icon size={20} /> : <Lock size={16} className="text-zinc-600" />}
        </div>
        <span className={`text-[9px] uppercase tracking-widest font-bold ${unlocked ? "" : "text-zinc-600"}`}>
          {a.tier}
        </span>
      </div>
      <div className="mt-3">
        <div className={`text-sm font-black ${unlocked ? "" : "text-zinc-400"}`}>{a.name}</div>
        <div className={`text-[11px] mt-1 leading-snug ${unlocked ? "text-zinc-300" : "text-zinc-500"}`}>{a.description}</div>
      </div>
      {!unlocked && progress && (
        <div className="mt-3" data-testid={`progress-${a.slug}`}>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            <span>progress</span>
            <span className="font-mono">{progress.current}/{progress.required}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-[var(--accent)]/60"
              style={{ width: `${Math.min(100, (progress.current / Math.max(1, progress.required)) * 100)}%` }}
            />
          </div>
        </div>
      )}
      {!unlocked && !progress && (
        <div
          className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500"
          data-testid={`progress-${a.slug}-event`}
        >
          Locked — earn in a single match
        </div>
      )}
      {unlocked && a.unlocked_at && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
          unlocked {new Date(a.unlocked_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
        </div>
      )}
    </motion.div>
  );
}

/** AchievementGrid — 4 columns desktop, 2 columns mobile. */
export function AchievementGrid({ unlocked = [], locked = [], compact = false }) {
  return (
    <div
      data-testid="achievement-grid"
      className={`grid gap-3 ${compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4"}`}
    >
      {unlocked.map((a) => (
        <AchievementTile key={a.slug} a={a} unlocked />
      ))}
      {locked.map((a) => (
        <AchievementTile key={a.slug} a={a} unlocked={false} progress={a.progress} />
      ))}
    </div>
  );
}

export default AchievementGrid;
